import { useState } from "react";
import { api, IMG } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  CheckCircle2,
  Sparkles,
  Brain,
  Video,
  BarChart3,
  Users,
  Heart,
  Calendar,
  Target,
  TrendingUp,
  Quote,
  ArrowUpRight,
  ArrowRight,
  FileText,
  Workflow,
  Megaphone,
  ChevronRight,
} from "lucide-react";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  current_brokerage: "",
  years_experience: "",
  production_level: "",
  dre_number: "",
  why_interested: "",
};

const EXPERIENCE = ["Just licensed", "Less than 1 year", "1–3 years", "3–5 years", "5+ years"];
const PRODUCTION = [
  "0–7 transactions / year",
  "8–15 transactions / year",
  "16–30 transactions / year",
  "31–50 transactions / year",
  "50+ transactions / year",
  "Team leader",
];

const WHY_JOIN = [
  { Icon: Brain, t: "AI-powered marketing", d: "Cutting-edge AI tools for content, listings, and lead nurture — built for agents, not engineers." },
  { Icon: Target, t: "Lead generation strategies", d: "A pipeline that compounds. Database systems, paid ads, and referrals working in concert." },
  { Icon: Sparkles, t: "Personal branding support", d: "We help you become the obvious choice in your market — visually, verbally, online." },
  { Icon: Video, t: "Video & social coaching", d: "Reels, shorts, TikTok, YouTube. The exact playbooks our top producers use weekly." },
  { Icon: Calendar, t: "Business planning & accountability", d: "Quarterly planning, weekly metrics, and a coach who keeps you honest." },
  { Icon: Users, t: "Training & masterminds", d: "Real-world skills sharpened in rooms full of producing agents." },
  { Icon: Heart, t: "Collaborative culture", d: "No cutthroat politics. Just agents who lift each other higher." },
  { Icon: TrendingUp, t: "Wealth-building opportunities", d: "Profit share, investing, team ownership. Multiple paths to long-term wealth." },
  { Icon: Workflow, t: "Flexible business models", d: "Solo agent, team, mega-team — design your business around your life." },
  { Icon: BarChart3, t: "Modern tech stack", d: "CRM, transaction management, AI assistants — all integrated, all included." },
];

const TECH = [
  { Icon: Brain, t: "AI content creation", d: "Listings, emails, captions, scripts — drafted in minutes, polished in seconds." },
  { Icon: Video, t: "Video marketing systems", d: "Templates, hooks, and editing flows that turn one shoot into a week of content." },
  { Icon: Workflow, t: "CRM automation", d: "Drip campaigns, smart follow-ups, and pipeline visibility — set it once, watch it work." },
  { Icon: BarChart3, t: "Database growth systems", d: "Plug-and-play playbooks to grow your sphere from 200 to 2,000 with intention." },
  { Icon: FileText, t: "Transaction management", d: "Templates, checklists, and tools so escrow runs like clockwork — every time." },
  { Icon: Megaphone, t: "Marketing templates", d: "Canva, social, postcard, email — all on-brand, all ready to ship." },
];

const COACHING = [
  { t: "Weekly coaching calls", d: "Real-time business help, hot seats, and momentum every single week." },
  { t: "Quarterly business planning", d: "Numbers, goals, gaps. Hard conversations that lead to bigger years." },
  { t: "One-on-one strategy", d: "Direct access to us — for the big decisions you don't outsource." },
  { t: "Group masterminds", d: "Producers, team leaders, and rising stars trading what's actually working right now." },
];

const FUNNEL = [
  { n: "01", t: "Discovery call", d: "20 minutes. We get to know each other and see if there's a fit." },
  { n: "02", t: "Business assessment", d: "We dig into your numbers, systems, and where the friction lives." },
  { n: "03", t: "Growth strategy session", d: "We build a clear plan for what doubling your business could look like with us." },
  { n: "04", t: "Transition plan", d: "If you decide to move — we make it seamless. Your business never skips a beat." },
];

const TESTIMONIALS = [
  {
    quote: "Joining Sonia's world changed everything. I went from 14 to 32 transactions in twelve months — and somehow had more time with my kids.",
    source: "Producing agent · South Bay",
  },
  {
    quote: "The AI tools alone are worth the move. I'm putting out 5x the content with half the work. The leads followed.",
    source: "Team leader · Westside LA",
  },
  {
    quote: "I felt stuck for two years at my old brokerage. The coaching here got me unstuck in two weeks.",
    source: "Mid-career agent · Calabasas",
  },
];

const CULTURE = [
  IMG.lifestyle1,
  IMG.lifestyle2,
  IMG.interior1,
  IMG.interior2,
];

const SEO_PILLS = [
  "Real estate careers · South Bay",
  "Brokerage · Los Angeles",
  "Best brokerage for experienced agents",
  "Growth systems · realtors",
  "AI for real estate agents",
];

export default function JoinTeam() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please share your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/team/apply", form);
      setSent(true);
      toast.success("You're in. We'll be in touch personally.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    document.querySelector("[data-testid=join-team-form-section]")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToWhy = (e) => {
    e.preventDefault();
    document.querySelector("[data-testid=join-team-why]")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="join-team-page" className="bg-white">
      {/* HERO — dark editorial */}
      <section
        className="relative min-h-[88vh] flex items-center overflow-hidden text-white"
        style={{ backgroundColor: "#0a2028" }}
        data-testid="join-team-hero"
      >
        <img
          src={IMG.lifestyle2}
          alt="Modern LA real estate"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a2028] via-[#1e4d5d]/70 to-[#2c5865]/50" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#e8c79a]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#e8c79a]/40 to-transparent" />

        <div className="container-edge relative w-full pt-32 pb-24">
          <div className="max-w-4xl">
            <p className="overline" style={{ color: "#e8c79a" }} data-testid="join-team-overline">
              For producing agents · Southern California
            </p>
            <h1
              className="mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[0.95] tracking-tight"
              style={{ fontFamily: "Playfair Display, serif", textShadow: "0 2px 30px rgba(0,0,0,0.5)" }}
              data-testid="join-team-headline"
            >
              Your next level<br />
              <em className="not-italic" style={{ color: "#e8c79a", fontStyle: "italic" }}>starts here.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/85 max-w-2xl font-light leading-relaxed">
              We help ambitious agents leverage AI, systems, coaching, and community to grow their
              business — while creating more freedom.
            </p>
            <p className="mt-5 text-base text-white/85 max-w-xl italic" style={{ fontFamily: "Playfair Display, serif" }}>
              Build a bigger business without sacrificing your life.
            </p>

            <div className="mt-12 flex flex-wrap gap-4">
              <a href="#form" onClick={scrollToForm} className="btn-secondary" data-testid="join-team-cta-primary">
                Book a confidential growth consultation <ArrowRight size={16} className="ml-2" />
              </a>
              <a
                href="#why"
                onClick={scrollToWhy}
                className="btn-outline border-white/70 text-white hover:bg-white hover:text-[#1e4d5d]"
                data-testid="join-team-cta-secondary"
              >
                See what we offer
              </a>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-8 max-w-2xl border-t border-white/15 pt-8">
              <div>
                <p className="text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif", color: "#e8c79a" }}>2×</p>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/80">Avg. production growth</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif", color: "#e8c79a" }}>AI</p>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/80">First marketing stack</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif", color: "#e8c79a" }}>1:1</p>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-white/80">Direct coaching access</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY AGENTS JOIN */}
      <section className="section" data-testid="join-team-why" id="why">
        <div className="max-w-3xl">
          <p className="overline">Why agents join</p>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
            Everything you need to scale.<br />Nothing you don't.
          </h2>
          <p className="mt-6 text-soft text-base md:text-lg leading-relaxed">
            For agents closing 8 to 50+ deals a year who are ready to compound their efforts —
            and reclaim their weekends in the process.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {WHY_JOIN.map(({ Icon, t, d }, i) => (
            <div
              key={t}
              className="border border-soft p-8 hover:border-black transition-colors duration-300 bg-white"
              data-testid={`join-why-${i}`}
            >
              <Icon size={22} className="text-sand" strokeWidth={1.25} />
              <h4 className="mt-6 text-lg" style={{ fontFamily: "Playfair Display, serif" }}>{t}</h4>
              <p className="mt-3 text-soft text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section
        className="section text-white relative overflow-hidden"
        style={{ backgroundColor: "#1e4d5d" }}
        data-testid="join-team-tech"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#e8c79a]/30 to-transparent" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="overline" style={{ color: "#e8c79a" }}>Technology</p>
            <h2 className="mt-4 text-4xl md:text-5xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              Built for the agent of 2026.
            </h2>
            <p className="mt-6 text-white/75 leading-relaxed">
              Real estate is changing. We arm our agents with the tools that actually move the
              needle — and the training to use them like an expert.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            {TECH.map(({ Icon, t, d }, i) => (
              <div key={t} className="flex gap-4" data-testid={`join-tech-${i}`}>
                <div className="shrink-0">
                  <div className="w-12 h-12 border border-[#e8c79a]/30 flex items-center justify-center">
                    <Icon size={20} style={{ color: "#e8c79a" }} strokeWidth={1.25} />
                  </div>
                </div>
                <div>
                  <h4 className="text-lg text-white" style={{ fontFamily: "Playfair Display, serif" }}>{t}</h4>
                  <p className="mt-2 text-white/80 text-sm leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COACHING */}
      <section className="section bg-linen" data-testid="join-team-coaching">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="overline">Coaching</p>
            <h2 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              A coach in your corner, every week.
            </h2>
            <p className="mt-6 text-soft leading-relaxed">
              Not a recorded course. Not a Slack channel. Real-time coaching from people who are
              still in the arena — and have built the kind of business you're trying to build.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {COACHING.map((c, i) => (
              <div key={c.t} className="bg-white border border-soft p-8" data-testid={`join-coaching-${i}`}>
                <p className="overline text-sand">{`0${i + 1}`}</p>
                <h4 className="mt-4 text-xl" style={{ fontFamily: "Playfair Display, serif" }}>{c.t}</h4>
                <p className="mt-3 text-soft text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUCCESS STORIES */}
      <section className="section" data-testid="join-team-stories">
        <div className="max-w-3xl">
          <p className="overline">Success stories</p>
          <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
            What "next level" actually looks like.
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={t.quote}
              className="bg-white border border-soft p-10 flex flex-col"
              data-testid={`join-testimonial-${i}`}
            >
              <Quote size={28} className="text-sand" strokeWidth={1.25} />
              <blockquote
                className="mt-6 text-xl md:text-2xl leading-snug flex-1"
                style={{ fontFamily: "Playfair Display, serif", fontStyle: "italic" }}
              >
                "{t.quote}"
              </blockquote>
              <figcaption className="overline mt-8">{t.source}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CULTURE */}
      <section className="section bg-linen" data-testid="join-team-culture">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-6">
            <p className="overline">Culture</p>
            <h2 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: "Playfair Display, serif" }}>
              Ambitious work.<br />Beautiful life.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-6">
            <p className="text-soft leading-relaxed">
              We work hard — and we celebrate hard. Beach mornings, masterminds in luxury
              listings, networking dinners, agent retreats, and a community that genuinely roots
              for each other's wins.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {CULTURE.map((src, i) => (
            <div key={src} className={`zoom-img overflow-hidden ${i === 0 ? "col-span-2 row-span-2 h-[420px] md:h-full" : "h-[200px] md:h-[280px]"}`}>
              <img src={src} alt="Culture" className="w-full h-full object-cover img-warm" />
            </div>
          ))}
        </div>
      </section>

      {/* RECRUITING FUNNEL */}
      <section
        className="section text-white"
        style={{ backgroundColor: "#1e4d5d" }}
        data-testid="join-team-process"
      >
        <div className="max-w-3xl">
          <p className="overline" style={{ color: "#e8c79a" }}>The process</p>
          <h2 className="mt-4 text-4xl md:text-5xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
            Confidential, calm, and clear.
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed">
            We never pressure. We don't broadcast. Every conversation stays between us — until
            you decide otherwise.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {FUNNEL.map((step, i) => (
            <div
              key={step.n}
              className="border border-white/15 p-8 relative bg-white/[0.02] backdrop-blur-sm"
              data-testid={`join-process-${i}`}
            >
              <p
                className="text-5xl"
                style={{ fontFamily: "Playfair Display, serif", color: "#e8c79a" }}
              >
                {step.n}
              </p>
              <h4 className="mt-6 text-xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>{step.t}</h4>
              <p className="mt-3 text-white/80 text-sm leading-relaxed">{step.d}</p>
              {i < FUNNEL.length - 1 && (
                <ChevronRight
                  size={20}
                  className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2"
                  style={{ color: "#e8c79a" }}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* LEAD CAPTURE — Blueprint + form */}
      <section className="section bg-white" data-testid="join-team-form-section" id="form">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
            <p className="overline" style={{ color: "#e8c79a" }}>Your free resource</p>
            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl" style={{ fontFamily: "Playfair Display, serif" }}>
              The 2026 Agent Growth Blueprint.
            </h2>
            <p className="mt-6 text-soft text-base md:text-lg leading-relaxed">
              7 systems top producers use to double their business — without doubling their
              hours. Yours free when you book your confidential growth consultation.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "The AI-content rhythm that fills your social calendar in 90 minutes a week",
                "The database multiplier that turns 200 contacts into 2,000",
                "The listing-side flywheel top producers run on autopilot",
                "How to take real time off without losing momentum",
              ].map((item, i) => (
                <li key={item} className="flex gap-3 text-sm md:text-base text-text">
                  <CheckCircle2 size={20} className="text-sand shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-soft">
              <p className="overline">100% confidential</p>
              <p className="mt-3 text-sm text-soft max-w-md">
                We never reach out to your current brokerage. Your name and details stay between
                you and Sonia.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div
                className="p-10 md:p-14 text-center text-white"
                style={{ backgroundColor: "#1e4d5d" }}
                data-testid="join-team-success"
              >
                <CheckCircle2 size={44} style={{ color: "#e8c79a" }} className="mx-auto" strokeWidth={1.25} />
                <p className="overline mt-8" style={{ color: "#e8c79a" }}>You're in</p>
                <h3 className="mt-4 text-4xl md:text-5xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                  Welcome to the conversation.
                </h3>
                <p className="mt-5 text-white/75 max-w-md mx-auto">
                  We review every application personally — usually within a couple of business
                  days. We'll send you the Blueprint and a link to grab time on our calendar.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="p-8 md:p-12 text-white"
                style={{ backgroundColor: "#1e4d5d" }}
                data-testid="join-team-form"
              >
                <p className="overline" style={{ color: "#e8c79a" }}>Book your consultation</p>
                <h3 className="mt-3 text-3xl md:text-4xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                  Let's talk growth.
                </h3>
                <p className="mt-3 text-white/80 text-sm">
                  A few details so we can show up prepared.
                </p>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="overline text-white/85">Full name *</label>
                    <input
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e8c79a] transition-colors"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your full name"
                      data-testid="join-team-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="overline text-white/85">Email *</label>
                    <input
                      type="email"
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e8c79a] transition-colors"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@email.com"
                      data-testid="join-team-email"
                      required
                    />
                  </div>
                  <div>
                    <label className="overline text-white/85">Phone *</label>
                    <input
                      type="tel"
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e8c79a] transition-colors"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="(555) 555-5555"
                      data-testid="join-team-phone"
                      required
                    />
                  </div>
                  <div>
                    <label className="overline text-white/85">Current brokerage</label>
                    <input
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e8c79a] transition-colors"
                      value={form.current_brokerage}
                      onChange={update("current_brokerage")}
                      placeholder="Where are you now?"
                      data-testid="join-team-brokerage"
                    />
                  </div>
                  <div>
                    <label className="overline text-white/85">Current production</label>
                    <select
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white focus:outline-none focus:border-[#e8c79a] transition-colors [&>option]:text-black"
                      value={form.production_level}
                      onChange={update("production_level")}
                      data-testid="join-team-production"
                    >
                      <option value="">Choose…</option>
                      {PRODUCTION.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline text-white/85">Years licensed</label>
                    <select
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white focus:outline-none focus:border-[#e8c79a] transition-colors [&>option]:text-black"
                      value={form.years_experience}
                      onChange={update("years_experience")}
                      data-testid="join-team-years"
                    >
                      <option value="">Choose…</option>
                      {EXPERIENCE.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline text-white/85">DRE # (optional)</label>
                    <input
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e8c79a] transition-colors"
                      value={form.dre_number}
                      onChange={update("dre_number")}
                      placeholder="DRE 00000000"
                      data-testid="join-team-dre"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="overline text-white/85">What's drawing you in? (optional)</label>
                    <textarea
                      className="w-full mt-2 bg-transparent border-0 border-b border-white/30 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#e8c79a] transition-colors min-h-[100px] resize-y"
                      value={form.why_interested}
                      onChange={update("why_interested")}
                      placeholder="A few sentences about what you're looking for…"
                      data-testid="join-team-why-textarea"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-10 inline-flex items-center justify-center px-8 py-4 text-sm font-medium tracking-wide w-full md:w-auto disabled:opacity-50 transition-all duration-300"
                  style={{ backgroundColor: "#e8c79a", color: "#1a2a30" }}
                  data-testid="join-team-submit"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="animate-spin mr-2" /> Sending…</>
                  ) : (
                    <>Book my consultation + send the Blueprint <ArrowUpRight size={14} className="ml-2" /></>
                  )}
                </button>
                <p className="mt-6 text-xs text-white/75">
                  100% confidential. We personally review every application.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* SEO keyword strip - subtle */}
      <section className="container-edge pb-12">
        <div className="flex flex-wrap gap-3 pt-12 border-t border-soft">
          {SEO_PILLS.map((s, i) => (
            <span
              key={s}
              className="text-[11px] tracking-[0.18em] uppercase text-soft border border-soft px-3 py-1.5"
            >
              {s}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
